import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';
import { compact, values } from 'lodash';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import subfeeDeleteFileApi from 'models/productManagement/subfee/deleteFile/Api.Subfee.DeleteFile';
import subfeeSaveApi from 'models/productManagement/subfee/save/Api.Subfee.Save';
import subfeeUploadFileApi from 'models/productManagement/subfee/uploadFile/Api.Subfee.UploadFile';
import { useMemo } from 'react';
import { useSubfeeProvider } from '../Subfee.Hooks';
import SubfeeDocumentTranslation from './edit/Subfee.DocumentTranslations';

export type SubfeeForm = Omit<Api.Core.Subfee.Save.IRequest, 'documents'> & {
  documents: Array<
    Utils.Unpacked<Api.Core.Subfee.Save.IRequest['documents']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedDocuments: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
};
export default function SubfeeEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, listApi } = useSubfeeProvider();

  const subfeeDocIds = useMemo(() => compact(selected?.documents?.map((d) => d.id)), [selected?.documents]);
  const listSubfeeDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'MediaFileSubfee', documentId: subfeeDocIds },
      wait: !subfeeDocIds.length,
    },
    [subfeeDocIds]
  );

  return (
    <ModalForm<SubfeeForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected, listSubfeeDocumentTranslationsApi?.payload]}
      title={lang.surcharge}
      default={{
        currency: 'CAD',
        amount: 0.0,
        ...selected?.toJSON(),
        documents: selected?.documents?.map((it) => ({
          ...it,
          translatedDocuments: listSubfeeDocumentTranslationsApi.payload?.data.filter((t) => t.documentId === it.id),
        })),
      }}
      onCancel={pageRouter.updateParams}
      onSubmit={async ({ documents, ...data }) => {
        const response = await subfeeSaveApi(data);
        if (response.status === HttpStatus.ok) {
          const subfeeId = response.payload || data.id;
          let totalFiles = 0;
          let uploadedFiles = 0;
          let deletedFiles = 0;
          let totalDeletedFiles = 0;

          for (const document of documents || []) {
            let documentId: null | number = document.id;
            if (document.isDeleted && document.id) {
              const docDeleteReponse = await subfeeDeleteFileApi(subfeeId, { documentId });
              if (docDeleteReponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            } else if (document.formFile) {
              const docUploadResponse = await subfeeUploadFileApi(subfeeId, {
                formFile: document.formFile.original,
                languageIsoCode: Language.english,
              });
              documentId = docUploadResponse?.payload;
              if (docUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }

            for (const translatedDoc of document.translatedDocuments || []) {
              if ((document.isDeleted || translatedDoc.isDeleted) && translatedDoc.id) {
                const docDeleteReponse = await documentTranslationDeleteApi({
                  id: translatedDoc.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (translatedDoc.formFile && !translatedDoc.id) {
                const docUploadResponse = await documentTranslationSaveApi({ ...translatedDoc, documentId });
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;
              }
            }
          }
          if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
            addLog({
              warning: combineStrings(
                ' ',
                lang.formSuccessAndFilesFailedMessage,
                totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null,
                totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} ${lang.filesGotDeleted}!` : null
              ),
            });
          } else {
            addLog({ success: lang.saveSuccess });
          }
        }

        if (response.status === HttpStatus.internalServerError) addLog({ error: response.message });
        listApi.execute((body) => body);
      }}
      validation={(data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.code) errors.code = lang.mustNotBeEmpty;
        if (data.amount == null) errors.amount = lang.mustNotBeEmpty;
        if (data.amount <= 0) errors.amount = lang.mustBeEqualOrMoreThanAmount.replace('{amount}', '0.00');
      }}
      render={({ form }) => (
        <>
          <FormWrapper>
            <TextInput
              name="code"
              label={lang.code}
              value={form.data.code}
              error={form.errors.code}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="name"
              label={lang.name}
              value={form.data.name}
              error={form.errors.name}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <NumberInput
              name="amount"
              label={lang.price}
              value={form.data.amount}
              error={form.errors.amount}
              onChange={form.update}
              icon={<Icon>{form.data.currency}</Icon>}
            />
            <InputWrapper style={{ marginBottom: Theme.Size.L }}>
              <FileInputSimple
                name="files"
                label={lang.documents}
                allowedMimeTypes={[MimeType.appPDF]}
                onChange={({ files }) => {
                  form.update({
                    documents: [
                      ...(form.data.documents ?? []),
                      ...files.map((file) => ({
                        formFile: file,
                        translatedDocuments: [],
                      })),
                    ],
                  });
                }}
              />
            </InputWrapper>
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th>{lang.fileName}</th>
                  <DataMap
                    data={values(Language)}
                    render={({ data: langIsoCode }) => <th key={langIsoCode}>{lang[langIsoCode]}</th>}
                  />
                </tr>
              </thead>
              <tbody>
                <DataMap
                  data={form.data.documents ?? []}
                  render={({ data: document, index }) =>
                    !document.isDeleted && (
                      <tr key={`${document.id ?? 0}-${index}`}>
                        <td>{document.mediaFile?.name ?? document.formFile?.original?.name ?? 'N/A'}</td>
                        <DataMap
                          data={values(Language)}
                          render={({ data: languageIsoCode }) => (
                            <td key={languageIsoCode}>
                              <SubfeeDocumentTranslation
                                languageIsoCode={languageIsoCode}
                                document={document}
                                index={index}
                              />
                            </td>
                          )}
                        />
                      </tr>
                    )
                  }
                />
              </tbody>
            </table>
          </FormWrapper>
        </>
      )}
    />
  );
}
